import sortBy from 'lodash/sortBy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { LanguageSelector } from '@/components/question/forms/LanguageSelector';
import { QuestionView } from '@/components/question/view/QuestionView';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge, Divider, PaginationControl } from '@/components/ui/daisy';
import { Question } from '@/models/Question';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
} from '@/models/TranslatableField';

type QuestionsPreviewProps = {
  questions: Question[];
  title?: string;
  className?: string;
};

export default function QuestionsPreview({
  questions,
  title,
  className,
}: QuestionsPreviewProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'questionsPreview',
  });
  const [page, setPage] = useState(1);
  const [language, setLanguage] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const orderedQuestions = sortBy(questions, 'order');
  const currentQuestion = orderedQuestions[page - 1];
  const total = orderedQuestions.length;
  const hasPagination = total > 1;
  const resolvedTitle = title ?? t('blockTitle');

  return (
    <section
      aria-label={resolvedTitle}
      className={twMerge('flex flex-col gap-4', className)}
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-base-content">
          {resolvedTitle}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <LanguageSelector
            activeLanguage={language}
            onLanguageChange={setLanguage}
          />
          <ConditionalRenderer condition={hasPagination}>
            <Badge size="sm" variant="soft">
              {t('pageIndicator', { current: page, total })}
            </Badge>
          </ConditionalRenderer>
        </div>
      </header>
      <ConditionalRenderer condition={!!currentQuestion}>
        <QuestionView
          question={currentQuestion}
          value={null}
          mode="reviewing"
          language={language}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={hasPagination}>
        <Divider className="my-0" />
        <PaginationControl
          className="self-center"
          color="primary"
          currentPage={page}
          totalPages={total}
          onPageChange={setPage}
          labels={{
            previous: t('previousQuestion'),
            next: t('nextQuestion'),
          }}
        />
      </ConditionalRenderer>
    </section>
  );
}
